import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Power,
  PowerOff,
  Monitor,
  Wifi,
  WifiOff,
  Clock,
  RefreshCw,
  Settings2,
  Calendar,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';

interface KioskInstance {
  id: string;
  name: string;
  is_online: boolean;
  last_heartbeat_at: string | null;
  local_ip_address: string | null;
  can_control_tvs: boolean;
  tv_count: number;
}

interface Display {
  id: string;
  name: string;
  is_active: boolean;
  last_heartbeat_at: string | null;
  last_power_state: string | null;
  last_power_state_at: string | null;
  power_schedule_enabled: boolean;
  power_on_time: string | null;
  power_off_time: string | null;
  power_days_of_week: number[] | null;
  controlled_by_kiosk_id: string | null;
  tv_brand: string | null;
  wol_enabled: boolean;
  api_enabled: boolean;
}

interface KioskControlPanelProps {
  businessId: string;
}

export function KioskControlPanel({ businessId }: KioskControlPanelProps) {
  const [kiosks, setKiosks] = useState<KioskInstance[]>([]);
  const [displays, setDisplays] = useState<Display[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingCommand, setSendingCommand] = useState(false);

  useEffect(() => {
    fetchData();

    // Subscribe to kiosk updates
    const kioskChannel = supabase
      .channel(`kiosk-status:${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'kiosk_instances',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          fetchData();
        }
      )
      .subscribe();

    // Subscribe to display updates
    const displayChannel = supabase
      .channel(`display-power:${businessId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'displays',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          fetchData();
        }
      )
      .subscribe();

    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);

    return () => {
      supabase.removeChannel(kioskChannel);
      supabase.removeChannel(displayChannel);
      clearInterval(interval);
    };
  }, [businessId]);

  const fetchData = async () => {
    try {
      // Fetch kiosks
      const { data: kioskData } = await supabase
        .from('kiosk_instances')
        .select('*')
        .eq('business_id', businessId)
        .order('name');

      setKiosks(kioskData || []);

      // Fetch displays with TV control info
      const { data: displayData } = await supabase
        .from('displays')
        .select('*')
        .eq('business_id', businessId)
        .order('name');

      setDisplays(displayData || []);
    } catch (err) {
      console.error('Failed to fetch kiosk data:', err);
    } finally {
      setLoading(false);
    }
  };

  const isKioskOnline = (kiosk: KioskInstance): boolean => {
    if (!kiosk.last_heartbeat_at) return false;
    const lastHeartbeat = new Date(kiosk.last_heartbeat_at);
    return Date.now() - lastHeartbeat.getTime() < 60000; // 1 minute
  };

  const sendCommand = async (action: string, data?: any) => {
    const onlineKiosk = kiosks.find(k => isKioskOnline(k));
    if (!onlineKiosk) {
      toast.error('No kiosk online', { description: 'Cannot send commands when kiosk is offline' });
      return;
    }

    setSendingCommand(true);
    try {
      // Send command via Supabase Realtime broadcast
      const channel = supabase.channel(`kiosk:${onlineKiosk.id}`);
      await channel.send({
        type: 'broadcast',
        event: 'command',
        payload: { action, data }
      });

      toast.success('Command sent', { description: `Sent ${action.replace('_', ' ')} command to kiosk` });
    } catch (error) {
      console.error('Failed to send command:', error);
      toast.error('Failed to send command');
    } finally {
      setSendingCommand(false);
    }
  };

  const hasOnlineKiosk = kiosks.some(k => isKioskOnline(k));
  const controllableDisplays = displays.filter(d => d.controlled_by_kiosk_id);
  const onDisplays = displays.filter(d => d.last_power_state === 'on');
  const offDisplays = displays.filter(d => d.last_power_state === 'off');

  const getDayName = (day: number): string => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[day] || '';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-32 bg-muted rounded" />
            <div className="h-20 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't show if no kiosks registered
  if (kiosks.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Settings2 className="w-4 h-4" />
              TV Control
            </CardTitle>
            <CardDescription className="mt-1">
              Control TVs via local kiosk
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {hasOnlineKiosk ? (
              <Badge variant="default" className="bg-green-500">
                <Wifi className="w-3 h-3 mr-1" />
                Kiosk Online
              </Badge>
            ) : (
              <Badge>
                <WifiOff className="w-3 h-3 mr-1" />
                Kiosk Offline
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Kiosk Status */}
        {kiosks.map(kiosk => (
          <div key={kiosk.id} className="flex items-center justify-between p-2 rounded-md bg-muted/50">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${isKioskOnline(kiosk) ? 'bg-green-500' : 'bg-gray-400'}`} />
              <div>
                <div className="text-sm font-medium">{kiosk.name}</div>
                <div className="text-xs text-muted-foreground">
                  {kiosk.tv_count} TV{kiosk.tv_count !== 1 ? 's' : ''} controlled
                  {kiosk.local_ip_address && ` • ${kiosk.local_ip_address}`}
                </div>
              </div>
            </div>
            {kiosk.last_heartbeat_at && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDistanceToNow(new Date(kiosk.last_heartbeat_at), { addSuffix: true })}
              </div>
            )}
          </div>
        ))}

        <Separator />

        {/* Power Status Summary */}
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Power className="w-4 h-4 text-green-500" />
            <span>{onDisplays.length} On</span>
          </div>
          <div className="flex items-center gap-2">
            <PowerOff className="w-4 h-4 text-gray-400" />
            <span>{offDisplays.length} Off</span>
          </div>
          <div className="flex items-center gap-2">
            <Monitor className="w-4 h-4 text-blue-500" />
            <span>{controllableDisplays.length} Controllable</span>
          </div>
        </div>

        {/* Power Actions */}
        <div className="flex gap-2">
          <Button
            variant="default"
            size="sm"
            disabled={!hasOnlineKiosk || sendingCommand}
            onClick={() => sendCommand('power_on_all')}
            className="flex-1"
          >
            {sendingCommand ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Power className="w-4 h-4 mr-2" />
            )}
            Turn On All TVs
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasOnlineKiosk || sendingCommand}
            onClick={() => sendCommand('power_off_all')}
            className="flex-1"
          >
            <PowerOff className="w-4 h-4 mr-2" />
            Turn Off All TVs
          </Button>
        </div>

        <Button
          variant="secondary"
          size="sm"
          disabled={!hasOnlineKiosk || sendingCommand}
          onClick={() => sendCommand('refresh_content')}
          className="w-full"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Content on All TVs
        </Button>

        {/* Power Schedule Info */}
        {displays.some(d => d.power_schedule_enabled) && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Power Schedules
              </div>
              {displays.filter(d => d.power_schedule_enabled).map(display => (
                <div key={display.id} className="text-xs text-muted-foreground p-2 bg-muted/30 rounded">
                  <div className="font-medium text-foreground">{display.name}</div>
                  <div>
                    On: {display.power_on_time || '08:00'} • Off: {display.power_off_time || '22:00'}
                  </div>
                  <div>
                    Days: {(display.power_days_of_week || [1,2,3,4,5]).map(getDayName).join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* No controllable TVs message */}
        {controllableDisplays.length === 0 && (
          <div className="text-center py-4 text-muted-foreground text-sm">
            <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No TVs linked to kiosk yet</p>
            <p className="text-xs mt-1">Use the kiosk app to discover and link TVs</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
